export type ContactMessageStatusType =
  | 'NEW'
  | 'READ'
  | 'IN_PROGRESS'
  | 'RESPONDED'
  | 'ARCHIVED'
  | 'SPAM';

export interface ContactMessageProps {
  id: string;
  fullName: string;
  email: string;
  phone?: string | null;
  company?: string | null;
  subject: string;
  message: string;
  status: ContactMessageStatusType;
  assignedToUserId?: string | null;
  assignedToUser?: {
    id: string;
    fullName: string;
    email: string;
  } | null;
  readAt?: Date | null;
  respondedAt?: Date | null;
  archivedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export class ContactMessage {
  private readonly _id: string;
  private _fullName: string;
  private _email: string;
  private _phone?: string | null;
  private _company?: string | null;
  private _subject: string;
  private _message: string;
  private _status: ContactMessageStatusType;
  private _assignedToUserId?: string | null;
  private _assignedToUser?: {
    id: string;
    fullName: string;
    email: string;
  } | null;
  private _readAt?: Date | null;
  private _respondedAt?: Date | null;
  private _archivedAt?: Date | null;
  private readonly _createdAt: Date;
  private _updatedAt: Date;

  private constructor(props: ContactMessageProps) {
    this._id = props.id;
    this._fullName = props.fullName;
    this._email = props.email;
    this._phone = props.phone;
    this._company = props.company;
    this._subject = props.subject;
    this._message = props.message;
    this._status = props.status;
    this._assignedToUserId = props.assignedToUserId;
    this._assignedToUser = props.assignedToUser;
    this._readAt = props.readAt;
    this._respondedAt = props.respondedAt;
    this._archivedAt = props.archivedAt;
    this._createdAt = props.createdAt;
    this._updatedAt = props.updatedAt;
  }

  public static create(props: {
    id: string;
    fullName: string;
    email: string;
    phone?: string | null;
    company?: string | null;
    subject: string;
    message: string;
    status?: ContactMessageStatusType;
    assignedToUserId?: string | null;
    assignedToUser?: {
      id: string;
      fullName: string;
      email: string;
    } | null;
    readAt?: Date | string | null;
    respondedAt?: Date | string | null;
    archivedAt?: Date | string | null;
    createdAt?: Date | string;
    updatedAt?: Date | string;
  }): ContactMessage {
    return new ContactMessage({
      id: props.id,
      fullName: props.fullName,
      email: props.email,
      phone: props.phone,
      company: props.company,
      subject: props.subject,
      message: props.message,
      status: props.status ?? 'NEW',
      assignedToUserId: props.assignedToUserId,
      assignedToUser: props.assignedToUser,
      readAt: props.readAt ? new Date(props.readAt) : null,
      respondedAt: props.respondedAt ? new Date(props.respondedAt) : null,
      archivedAt: props.archivedAt ? new Date(props.archivedAt) : null,
      createdAt: props.createdAt ? new Date(props.createdAt) : new Date(),
      updatedAt: props.updatedAt ? new Date(props.updatedAt) : new Date(),
    });
  }

  public get id(): string {
    return this._id;
  }

  public get fullName(): string {
    return this._fullName;
  }

  public get email(): string {
    return this._email;
  }

  public get phone(): string | null | undefined {
    return this._phone;
  }

  public get company(): string | null | undefined {
    return this._company;
  }

  public get subject(): string {
    return this._subject;
  }

  public get message(): string {
    return this._message;
  }

  public get status(): ContactMessageStatusType {
    return this._status;
  }

  public get assignedToUserId(): string | null | undefined {
    return this._assignedToUserId;
  }

  public get assignedToUser() {
    return this._assignedToUser;
  }

  public get readAt(): Date | null | undefined {
    return this._readAt;
  }

  public get respondedAt(): Date | null | undefined {
    return this._respondedAt;
  }

  public get archivedAt(): Date | null | undefined {
    return this._archivedAt;
  }

  public get createdAt(): Date {
    return this._createdAt;
  }

  public get updatedAt(): Date {
    return this._updatedAt;
  }
}
